import { IMAGES } from "./assets";
import {
  Waves,
  Leaf,
  Mountain,
  Fish,
  ScanLine,
  Map as MapIcon,
  Route,
} from "lucide-react";

export const SERVICES = [
  {
    slug: "bathymetric",
    icon: Waves,
    title: "Bathymetric / Depth Mapping",
    short: "Detailed, accurate depth maps of any waterbody.",
    image: IMAGES.bathymetric,
    desc: "We map the underwater terrain of your lake, pond, or reservoir with survey-grade sonar, producing precise depth contours and volume calculations you can act on.",
    deliverables: ["Depth contour maps", "Volumetric / storage calculations", "Max & mean depth stats", "GIS-ready layers"],
  },
  {
    slug: "vegetation",
    icon: Leaf,
    title: "Aquatic Vegetation Mapping",
    short: "Plant presence, coverage and biovolume for lake health.",
    image: IMAGES.vegetation,
    desc: "Identify where aquatic plants grow, how dense they are, and how coverage changes over time — the foundation for effective vegetation management.",
    deliverables: ["Presence / absence maps", "Biovolume percentage", "Coverage area totals", "Year-over-year comparisons"],
  },
  {
    slug: "bottom-hardness",
    icon: Mountain,
    title: "Bottom Composition & Hardness",
    short: "Map soft sediment vs. firm bottom across the basin.",
    image: IMAGES.bottomHardness,
    desc: "Understand your lakebed. Our hardness mapping distinguishes soft, muck-laden zones from firm bottom to support dredging, habitat and shoreline decisions.",
    deliverables: ["Hardness classification maps", "Sediment zone identification", "Dredging planning support", "Substrate analysis"],
  },
  {
    slug: "fish-habitat",
    icon: Fish,
    title: "Fish & Habitat Mapping",
    short: "Fish counts, depths, sizes and density.",
    image: IMAGES.fishHabitat,
    desc: "Sonar-derived fish and habitat insights show where fish hold, at what depths, and how densely — invaluable for fisheries and habitat management.",
    deliverables: ["Fish density maps", "Depth distribution", "Relative size estimates", "Habitat structure mapping"],
  },
  {
    slug: "side-scan",
    icon: ScanLine,
    title: "Side-Scan Imagery & Contours",
    short: "High-resolution imagery of submerged features.",
    image: IMAGES.sidescan,
    desc: "Side-scan sonar reveals submerged structure, debris, and bottom texture in vivid detail, paired with contour mapping for a complete subsurface picture.",
    deliverables: ["Side-scan mosaics", "Structure / debris identification", "Contour overlays", "High-resolution imagery"],
  },
  {
    slug: "gis",
    icon: MapIcon,
    title: "GIS Map Services",
    short: "Custom digital and print maps for stakeholders.",
    image: IMAGES.gisDeliverable,
    desc: "We turn survey data into polished, shareable deliverables — from interactive GIS layers to large-format printed maps for boards, districts and residents.",
    deliverables: ["GeoTIFF & shapefile exports", "Large-format print maps", "Interactive web maps", "Custom cartography"],
  },
  {
    slug: "survey-planning",
    icon: Route,
    title: "Survey Planning",
    short: "Systematic transect planning for full coverage.",
    image: IMAGES.bathymetric,
    desc: "Every project starts with a plan. We design transect patterns and GPS coverage to ensure thorough, repeatable data across the entire waterbody.",
    deliverables: ["Transect design", "GPS coverage plan", "Access & logistics plan", "Repeatable survey grids"],
  },
];

export const SOLUTIONS = [
  {
    slug: "water-districts",
    title: "Water Resources & Reservoirs",
    audience: "Water districts & municipalities",
    pains: [
      "Track reservoir storage capacity and sedimentation over time",
      "Plan dredging with accurate volume and hardness data",
      "Document conditions for regulatory and public reporting",
    ],
  },
  {
    slug: "community-lakes",
    title: "Community & Municipal Lakes",
    audience: "Parks, cities & governing bodies",
    pains: [
      "Understand depth and vegetation for recreation and safety",
      "Guide fisheries and habitat improvement programs",
      "Share clear maps with councils and the public",
    ],
  },
  {
    slug: "hoas",
    title: "HOAs & Lake Associations",
    audience: "Associations & lake boards",
    pains: [
      "Justify vegetation and dredging budgets with real data",
      "Monitor lake health year over year",
      "Give residents professional, easy-to-read maps",
    ],
  },
  {
    slug: "homeowners",
    title: "Homeowners & Private Ponds",
    audience: "Private pond & lake owners",
    pains: [
      "Know the true depth and volume of your pond",
      "Plan stocking, aeration or dredging with confidence",
      "Get a beautiful map of your own waterbody",
    ],
  },
];

export const PROCESS = [
  {
    step: "01",
    title: "Consult & Plan",
    desc: "We learn your goals, then design a survey plan and transect coverage tailored to your waterbody.",
  },
  {
    step: "02",
    title: "On-Site Data Collection",
    desc: "Our team travels to your site and collects survey-grade sonar and GPS data from our boat — you don't lift a finger.",
  },
  {
    step: "03",
    title: "Processing & Review",
    desc: "Data is processed and quality-reviewed by our hydrographers and GIS specialists for accuracy.",
  },
  {
    step: "04",
    title: "Delivered Maps & Reports",
    desc: "You receive finished, cloud-accessible maps, data layers, and a clear report ready to share.",
  },
];

export const STATS = [
  { value: "500+", label: "Waterbodies Surveyed" },
  { value: "Survey-Grade", label: "Depth Accuracy" },
  { value: "100%", label: "GIS-Ready Deliverables" },
  { value: "US & CA", label: "Full Coverage" },
];
